import { Texture, BaseTexture } from 'pixi.js';
import { mediaSourceDimensions } from './MediaSourceDimensionTracker';
import ReactiveMedia from './ReactiveMedia';
import { ProcessSpectrum, GetSpectrumMagnitude } from '../../ProcessSpectrum';
import WebCodecsBuffer from './WebCodecsBuffer';
import { BackgroundVideoElementInstance } from '../../BackgroundVideoElement';

const VIDEO_ACCELERATION_MULTIPLIER = 200;

export default class ReactiveVideo extends ReactiveMedia {
    constructor(stage, zIndex) {
        super(stage, zIndex);
        this._destroyed = false;
        this.videoAccelerationEnabled = false;
        this.previewResolution = null;
        this._performanceFallback = false;

        this.virtualVideoTime = 0;
        this.currentSpeed = 1.0;
        this.lastDrawTime = performance.now();

        this.speedUpFactor = 0;

        this.webCodecsBuffer = null;
        this.lastBitmap = null;
        this.baseTexture = null;

        this.canvas = document.createElement('canvas');
        this.ctx = this.canvas.getContext('2d');

        this.drawingSourceMode = 'video';
        this.videoTextureInitialized = false;

        this.sprite.texture = Texture.EMPTY;
    }

    update(config) {
        const { mediaSource, videoAcceleration, previewResolution, onLoadingStateChange, onPerformanceFallback, backgroundVideoShouldPlay } = config;
        const accelerationEnabled = videoAcceleration?.enabled || false;
        const accelerationIntensity = videoAcceleration?.intensity ?? 50;
        const mediaSourceChanged = this.mediaSource && this.mediaSource !== mediaSource;
        const previewResolutionChanged = this.previewResolution !== previewResolution;
        const accelerationJustEnabled = accelerationEnabled && !this.videoAccelerationEnabled;

        this.onLoadingStateChange = onLoadingStateChange;
        this.onPerformanceFallback = onPerformanceFallback;
        this.backgroundVideoShouldPlay = Boolean(backgroundVideoShouldPlay);


        if (mediaSourceChanged || previewResolutionChanged || accelerationJustEnabled) {
            if (this._performanceFallback && this.onPerformanceFallback) {
                this.onPerformanceFallback(false);
            }
            this._performanceFallback = false;
        }

        const targetMode = (accelerationEnabled && !this._performanceFallback) ? 'webcodecs' : 'video';

        this.videoAccelerationEnabled = accelerationEnabled;
        this.previewResolution = previewResolution;

        this.speedUpFactor = (accelerationIntensity / 100) * VIDEO_ACCELERATION_MULTIPLIER;

        super.update(config);

        const modeChanged = this.drawingSourceMode !== targetMode;
        const needsReinit = mediaSourceChanged || previewResolutionChanged || modeChanged;

        if (targetMode === 'webcodecs') {
            if (needsReinit || !this.webCodecsBuffer) {
                if (this.webCodecsBuffer) {
                    this.webCodecsBuffer.destroy();
                    this.webCodecsBuffer = null;
                }
                this.lastBitmap = null;
                this.virtualVideoTime = 0;
                this.currentSpeed = 1.0;

                this.mediaSource = mediaSource;

                const data = mediaSource?.data || {};
                const videoUrl = this.selectVideoUrl(data, previewResolution);

                if (!videoUrl) {
                    return; 
                }

                if (this.onLoadingStateChange) {
                    this.onLoadingStateChange(true);
                }

                const videoEl = BackgroundVideoElementInstance;
                const wasPlaying = videoEl && !videoEl.paused;
                if (videoEl) videoEl.pause();

                this.webCodecsBuffer = new WebCodecsBuffer(
                    videoUrl, 
                    () => {
                        if (this._destroyed) {
                            if (wasPlaying && BackgroundVideoElementInstance) {
                                BackgroundVideoElementInstance.play().catch(() => {});
                            }
                            return;
                        }

                        const firstBitmap = this.webCodecsBuffer.getFirstFrame();
                        if (firstBitmap) {
                            this.canvas.width = firstBitmap.width;
                            this.canvas.height = firstBitmap.height;

                            if (this.baseTexture) {
                                this.baseTexture.destroy();
                            }
                            
                            this.baseTexture = BaseTexture.from(this.canvas);
                            this.sprite.texture = new Texture(this.baseTexture);

                            mediaSourceDimensions[videoUrl] = {
                                width: firstBitmap.width,
                                height: firstBitmap.height
                            };
                            this.correctAspectRatio(firstBitmap.width, firstBitmap.height);
                            this.refreshAfterSourceDimensionsChange();

                            this.ctx.drawImage(firstBitmap, 0, 0);
                            this.baseTexture.update();

                            this.drawingSourceMode = 'webcodecs';
                            this.videoTextureInitialized = false;
                            this.sprite.visible = true;
                        }

                        if (this.onLoadingStateChange) {
                            this.onLoadingStateChange(false);
                        }
                    },
                    (error) => {
                        if (this._destroyed) return;

                        console.error('[ReactiveVideo] WebCodecs initialization failed:', error);

                        if (wasPlaying && BackgroundVideoElementInstance) {
                            BackgroundVideoElementInstance.play().catch(() => {});
                        }

                        this.sprite.visible = true;

                        if (this.onLoadingStateChange) {
                            this.onLoadingStateChange(false);
                        }
                    },
                    () => this._handlePerformanceFallback()
                );
            }
        } else {
            if (needsReinit || !this.videoTextureInitialized) {
                this.cleanupResources();
                this.drawingSourceMode = 'video';
                this.mediaSource = mediaSource;
                this.videoTextureInitialized = false;

                this.applyBackgroundVideoPlayback();
            }
            this.sprite.visible = true;
        }
    }

    // The backdrop <video> is shared with BackgroundVideoElement, which owns the rule that it runs only
    // while the preview does. This applies that same rule rather than a second one of its own.
    //
    // What used to be here was an unconditional play(), meant to make the element decode a frame for
    // drawFromVideoElement to sample. It left the video running behind a paused preview, and since the
    // canvas only samples it when something forces a redraw, the backdrop sat on a stale frame until
    // the next settings change resampled a video that had moved on by seconds. Dragging a picture ends
    // in exactly one such redraw, which is why the backdrop appeared to change when images were moved.
    // The element does not need the kick: BackgroundVideoElement loads it and seeks it to the first
    // frame whenever the source changes, which decodes one.
    applyBackgroundVideoPlayback() {
        const videoEl = BackgroundVideoElementInstance;

        if (!videoEl)
            return;

        if (this.backgroundVideoShouldPlay && videoEl.paused)
            videoEl.play().catch(() => {});
        else if (!this.backgroundVideoShouldPlay && !videoEl.paused)
            videoEl.pause();
    }

    cleanupResources() {
        if (this.webCodecsBuffer) {
            this.webCodecsBuffer.destroy();
            this.webCodecsBuffer = null;

            if (this.onLoadingStateChange)
                this.onLoadingStateChange(false);
        }
        this.lastBitmap = null;
        this.virtualVideoTime = 0;
        this.currentSpeed = 1.0;
        this.videoTextureInitialized = false;
    }

    _handlePerformanceFallback() {
        if (this._destroyed || this._performanceFallback) return;

        this._performanceFallback = true;

        this.cleanupResources();
        this.drawingSourceMode = 'video';
        this.videoTextureInitialized = false;

        this.applyBackgroundVideoPlayback();
        this.sprite.visible = true;

        if (this.onLoadingStateChange) this.onLoadingStateChange(false);
        if (this.onPerformanceFallback) this.onPerformanceFallback(true);
    }

    selectVideoUrl(data, previewResolution) {
        const maxAllowedResolution = 480;
        const targetResolution = Math.min(previewResolution || 480, maxAllowedResolution);

        if (targetResolution >= 480) {
            return data.p540Url || data.p360Url || data.url;
        } else if (targetResolution >= 360) {
            return data.p360Url || data.p540Url || data.url;
        } else {
            return data.p360Url || data.url;
        }
    }

    draw(spectrum, freezeVideoBackground, previewRunning) {
        super.draw(spectrum);

        if (this.drawingSourceMode === 'webcodecs') {
            this.drawFromWebCodecsBuffer(spectrum, freezeVideoBackground, previewRunning);
        } else {
            this.drawFromVideoElement();
        }
    }

    drawFromVideoElement() {
        const videoEl = BackgroundVideoElementInstance;
        if (!videoEl || !videoEl.videoWidth || !videoEl.videoHeight) return;

        if (!this.videoTextureInitialized) {
            this.canvas.width = videoEl.videoWidth;
            this.canvas.height = videoEl.videoHeight;

            if (this.baseTexture) {
                this.baseTexture.destroy();
            }

            this.baseTexture = BaseTexture.from(this.canvas);
            this.sprite.texture = new Texture(this.baseTexture);

            mediaSourceDimensions[videoEl.src] = {
                width: videoEl.videoWidth,
                height: videoEl.videoHeight
            };
            this.correctAspectRatio(videoEl.videoWidth, videoEl.videoHeight);
            this.refreshAfterSourceDimensionsChange();

            this.videoTextureInitialized = true;
        }

        this.ctx.drawImage(videoEl, 0, 0, this.canvas.width, this.canvas.height);
        if (this.baseTexture) {
            this.baseTexture.update();
        }
    }

    drawFromWebCodecsBuffer(spectrum, freezeVideoBackground, previewRunning) {
        if (!this.webCodecsBuffer || this.webCodecsBuffer.frameCount === 0) return;

        const now = performance.now();
        let deltaMS = now - this.lastDrawTime;

        if (deltaMS > 100) deltaMS = 16.6;
        this.lastDrawTime = now;

        // The accelerated backdrop keeps its own clock, advanced by wall-clock time rather than by the
        // playback position. It is held when the background is frozen, and equally when the preview is
        // not running: a paused preview still redraws on every settings change, and advancing on those
        // would walk the backdrop forward while the user is only editing.
        if (!freezeVideoBackground && previewRunning) {
            const processedSpectrum = ProcessSpectrum(spectrum, this.spectrumOptions);
            const spectrumMagnitude = GetSpectrumMagnitude(processedSpectrum);

            this.currentSpeed = this.videoAccelerationEnabled
                ? 1 + (spectrumMagnitude / 255) * this.speedUpFactor
                : 1.0;

            const deltaSeconds = deltaMS / 1000;
            this.virtualVideoTime += deltaSeconds * this.currentSpeed;
        }

        const bitmap = this.webCodecsBuffer.getFrameAtTime(this.virtualVideoTime);
        if (bitmap) {
            if (bitmap !== this.lastBitmap) {
                this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
                this.ctx.drawImage(bitmap, 0, 0);
                this.lastBitmap = bitmap;
            }
            if (this.baseTexture) {
                this.baseTexture.update();
            }
        }
    }

    updateStage(stage) {
        super.updateStage(stage);
    }

    destroy() {
        this._destroyed = true;
        this.cleanupResources();

        if (this.sprite) {
            if (this.sprite.parent) {
                this.sprite.parent.removeChild(this.sprite);
            }
            const oldTexture = this.sprite.texture;
            this.sprite.texture = Texture.EMPTY;
            if (oldTexture && oldTexture !== Texture.EMPTY) {
                Texture.removeFromCache(oldTexture);
                oldTexture.destroy();
            }
        }

        if (this.baseTexture) {
            this.baseTexture.destroy();
            this.baseTexture = null;
        }

        if (super.destroy) {
            super.destroy();
        }
    }
}